import { useMemo, useState } from 'react'
import { CalendarDays, CheckCircle2, Clock3, Loader2, LockKeyhole, Phone, ShieldCheck, Video } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { api, loadRazorpay } from '../lib/api'
import type { Astrologer, Booking, Language } from '../types'

type Props = { language: Language; astrologer: Astrologer; initialMode?: 'call' | 'video' | 'chat'; onLogin: () => void }
type OrderResponse = { booking: Booking; order: { id: string; amount: number; currency: string }; keyId: string }

export function BookingPanel({ language, astrologer, initialMode = 'call', onLogin }: Props) {
  const en = language === 'en'
  const { user } = useAuth()
  const navigate = useNavigate()
  const [mode, setMode] = useState<'call' | 'video' | 'chat'>(initialMode)
  const [duration, setDuration] = useState(20)
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [time, setTime] = useState('18:00')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const rate = mode === 'chat' ? astrologer.priceChat : astrologer.priceCall
  const amount = useMemo(() => rate * duration, [rate, duration])

  const pay = async () => {
    if (!user) { onLogin(); return }
    setBusy(true); setError('')
    try {
      const scheduledAt = new Date(`${date}T${time}:00`).toISOString()
      const booking = await api.post<{ booking: Booking }>('bookings', { astrologerId: astrologer.id, type: mode, durationMinutes: duration, scheduledAt })
      const result = await api.post<OrderResponse>('create-order', { bookingId: booking.booking.id })
      const loaded = await loadRazorpay()
      if (!loaded) throw new Error('Razorpay checkout load नहीं हुआ। Internet connection जाँचें।')
      const checkout = new window.Razorpay({
        key: result.keyId,
        amount: result.order.amount,
        currency: result.order.currency,
        name: 'Mokshm',
        description: `${astrologer.name} • ${duration} min ${mode}`,
        image: `${window.location.origin}/mokshm-logo.png`,
        order_id: result.order.id,
        prefill: { contact: user.phoneNumber || '' },
        theme: { color: '#e85f22' },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (response: unknown) => {
          const verified = await api.post<{ booking: Booking }>('verify-payment', response)
          navigate(`/dashboard?paid=${verified.booking.id}`)
        },
      })
      checkout.on('payment.failed', () => { setError('Payment पूरा नहीं हुआ। आप दोबारा कोशिश कर सकते हैं।'); setBusy(false) })
      checkout.open()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Booking नहीं बन पाई।')
      setBusy(false)
    }
  }

  return <aside className="booking-panel">
    <p className="eyebrow">BOOK PRIVATE SESSION</p>
    <h2>{en ? 'Choose your consultation' : 'अपना consultation चुनें'}</h2>
    <div className="mode-tabs">
      <button className={mode === 'call' ? 'active' : ''} onClick={() => setMode('call')}><Phone /> Call</button>
      <button className={mode === 'video' ? 'active' : ''} onClick={() => setMode('video')}><Video /> Video</button>
      <button className={mode === 'chat' ? 'active' : ''} onClick={() => setMode('chat')}>Chat</button>
    </div>
    <label className="field-label"><Clock3 size={15} /> {en ? 'Duration' : 'समय'}</label>
    <div className="duration-options">{[10, 20, 30, 60].map((value) => <button key={value} className={duration === value ? 'active' : ''} onClick={() => setDuration(value)}>{value} min</button>)}</div>
    <label className="field-label"><CalendarDays size={15} /> {en ? 'Date and time' : 'दिन और समय'}</label>
    <div className="date-time"><input type="date" min={new Date().toISOString().slice(0, 10)} value={date} onChange={(e) => setDate(e.target.value)} /><input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
    <div className="bill"><span>₹{rate}/min × {duration} min</span><strong>₹{amount}</strong></div>
    <button className="primary-button full payment-button" disabled={busy} onClick={pay}>{busy ? <Loader2 className="spin" /> : user ? <><LockKeyhole size={17} /> Secure payment</> : en ? 'Login to book' : 'Login करके book करें'}</button>
    {error && <div className="form-error">{error}</div>}
    <ul className="assurance-list"><li><ShieldCheck /> {en ? 'Payment verified on the server' : 'Payment server पर verify होगा'}</li><li><CheckCircle2 /> {en ? 'Session closes automatically when time ends' : 'समय खत्म होने पर session अपने-आप बंद'}</li><li><CheckCircle2 /> {en ? 'Your information remains private' : 'आपकी जानकारी private रहेगी'}</li></ul>
  </aside>
}
